import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import Dialog from "./ui/Dialog";
import OtpInput from "./ui/OtpInput";

const LoginPaid = ({ isOpen, onClose, onLoginSuccess, onNewUser }) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState(Array(6).fill("")); // OTP input state
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [otpErrorMessage, setOTPErrorMessage] = useState("");
  const [otpsuccessMessage, setOTPSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false); // Track if OTP has been sent
  const [resendCooldown, setResendCooldown] = useState(0); // seconds left until user can resend
  const navigate = useNavigate();
  const { login } = useAuth();
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  // Resend OTP countdown {"\u2014"} 30 sec lock after each send
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(
      () => setResendCooldown((s) => (s > 0 ? s - 1 : 0)),
      1000,
    );
    return () => clearInterval(t);
  }, [resendCooldown]);

  const handleClose = () => {
    setPhoneNumber("");
    setOtp(Array(6).fill(""));
    setOtpSent(false);
    setErrorMessage("");
    setSuccessMessage("");
    setOTPErrorMessage("");
    setOTPSuccessMessage("");
    setLoading(false);
    setResendCooldown(0);

    onClose(); // call parent close
  };
  if (!isOpen) return null;

  // Handle sending phone number for OTP
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (phoneNumber.length !== 10) {
      setErrorMessage("Phone number must be exactly 10 digits");
      return;
    }
    setLoading(true); // Show loader
    try {
      const response = await fetch(`${BASE_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "https://api.bossinvestor.in",
          "Access-Control-Allow-Credentials": "true",
        },
        body: JSON.stringify({ phoneNumber }),
      });

      const result = await response.json();
      setLoading(false); // Hide loader

      // Check if the response is a success
      if (!response.ok) {
        const errorDetails =
          result.status?.errors?.phoneNumber || result.status.message;
        setErrorMessage(
          errorDetails || "Failed to send OTP. Please try again.",
        );
        return;
      }

      // If OTP request is successful, show OTP input fields
      setOtpSent(true);
      setErrorMessage("");
      setOTPErrorMessage("");
      setSuccessMessage("OTP sent successfully!");
      setResendCooldown(30); // start 30s cooldown
    } catch (error) {
      setLoading(false); // Hide loader
      setErrorMessage(error.message || "An unexpected error occurred");
    }
  };

  // Re-send OTP {"\u2014"} same endpoint, gated by cooldown
  const handleResend = async () => {
    if (resendCooldown > 0 || loading) return;
    setLoading(true);
    setOTPErrorMessage("");
    setOTPSuccessMessage("");
    try {
      const response = await fetch(`${BASE_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "https://api.bossinvestor.in",
          "Access-Control-Allow-Credentials": "true",
        },
        body: JSON.stringify({ phoneNumber }),
      });
      const result = await response.json();
      setLoading(false);
      if (!response.ok) {
        setOTPErrorMessage(
          result?.status?.errors?.phoneNumber ||
            result?.status?.message ||
            "Could not resend OTP. Try again.",
        );
        return;
      }
      setOtp(Array(6).fill(""));
      setOTPSuccessMessage("New OTP sent to your mobile.");
      setResendCooldown(30);
    } catch (err) {
      setLoading(false);
      setOTPErrorMessage(err.message || "Could not resend OTP.");
    }
  };

  // Handle OTP verification
  const handleOtpVerify = async (e) => {
    e.preventDefault();
    const otpCode = otp.join("");
    if (otpCode.length !== 6) {
      setOTPErrorMessage("OTP must be exactly 6 digits");
      return;
    }
    setLoading(true); // Show loader
    try {
      const response = await fetch(`${BASE_URL}/auth/verifyLoginOtp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "https://api.bossinvestor.in",
          "Access-Control-Allow-Credentials": "true",
        },
        body: JSON.stringify({ phoneNumber, otp: otpCode }),
      });

      const result = await response.json();
      setLoading(false); // Hide loader

      if (!response.ok || result?.status?.statusCode !== 200) {
        throw new Error(result?.status?.message || "OTP verification failed.");
      }

      setOTPErrorMessage("");

      // First-time user: verify succeeds and returns a jwtToken but no user
      // profile yet. Don't log in - persist the token (authorizes /user/register)
      // and hand off to the registration screen instead of crashing on user.id.
      if (!result.data.user) {
        window.sessionStorage.setItem("jwtToken", result.data.jwtToken);
        window.sessionStorage.setItem("refreshToken", result.data.refreshToken);
        setOTPSuccessMessage("OTP verified! Complete your registration.");
        onNewUser?.(phoneNumber);
        return;
      }

      setOTPSuccessMessage("Login successful!");
      login(result.data.jwtToken, result.data.user);
      window.sessionStorage.setItem("userId", result.data.user.id);
      window.sessionStorage.setItem("refreshToken", result.data.refreshToken);
      window.sessionStorage.setItem(
        "userName",
        result.data.user.firstName + " " + result.data.user.lastName,
      );

      onLoginSuccess();
      navigate("/", {
        replace: true,
        state: { sessionExpired: false },
      });
    } catch (error) {
      setLoading(false); // Hide loader
      setOTPErrorMessage(error.message || "OTP verification failed.");
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      eyebrow={otpSent ? "Verify mobile" : "Sign in"}
      title={otpSent ? "Enter the 6-digit OTP" : "Enter your mobile number"}
      subtitle={
        otpSent ? (
          <>
            Sent to <strong className="fn-num">+91 {phoneNumber}</strong>.
          </>
        ) : (
          "We’ll send a 6-digit OTP to verify it’s you."
        )
      }
      titleId="lp-title"
    >
      {!otpSent ? (
        <form onSubmit={handleLoginSubmit} className="fn-auth-form">
          <div className="fn-auth-phone">
            <span className="fn-auth-prefix fn-num" aria-hidden="true">+91</span>
            <input
              type="tel"
              inputMode="numeric"
              maxLength="10"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ""))}
              placeholder="10-digit mobile number"
              className="fn-field fn-num"
              autoComplete="tel-national"
              aria-labelledby="lp-title"
              aria-invalid={errorMessage ? true : undefined}
              aria-describedby={errorMessage ? "lp-phone-error" : undefined}
              autoFocus
            />
          </div>

          <button
            type="submit"
            className="fn-btn fn-btn-primary fn-btn-block"
            disabled={loading || phoneNumber.length !== 10}
          >
            {loading ? "Sending..." : "Send OTP"}
          </button>

          {errorMessage && (
            <div className="fn-error" id="lp-phone-error" role="alert">
              {errorMessage}
            </div>
          )}
          {successMessage && (
            <div className="fn-success" role="status">
              {successMessage}
            </div>
          )}

          <p className="fn-dlg-note">By continuing you agree to our Terms &amp; Privacy.</p>
        </form>
      ) : (
        <div className="fn-auth-form">
          <OtpInput length={6} otp={otp} setOtp={setOtp} labelledBy="lp-title" />

          <button
            type="button"
            onClick={handleOtpVerify}
            className="fn-btn fn-btn-primary fn-btn-block"
            disabled={loading || otp.join("").length !== 6}
          >
            {loading ? "Verifying..." : "Verify OTP"}
          </button>

          <div className="fn-auth-resend">
            {resendCooldown > 0 ? (
              <span className="fn-help fn-num">
                Didn{"’"}t get it? Resend in {resendCooldown}s
              </span>
            ) : (
              <button
                type="button"
                className="fn-btn fn-btn-quiet fn-btn-sm"
                onClick={handleResend}
                disabled={loading}
              >
                Resend OTP
              </button>
            )}
          </div>

          {otpErrorMessage && (
            <div className="fn-error" role="alert">
              {otpErrorMessage}
            </div>
          )}
          {otpsuccessMessage && (
            <div className="fn-success" role="status">
              {otpsuccessMessage}
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
};

export default LoginPaid;
